import { Trans, useTranslation } from 'react-i18next';
import { Card, Divider } from '@knack/asterisk-react';

import type { ProductPlan } from '@/types/account';
import { PLAN_FREQUENCY, type PlanFrequency } from '@/types/plans';
import { cn } from '@/utils/tailwind';
import { SelectPlanButton } from '@/pages/settings/billing/plans/SelectPlanButton';

interface PlanCardBaseProps {
  knackPlan: ProductPlan;
  originalPlan: ProductPlan | undefined;
  planFrequency: PlanFrequency;
  isDowngradeAllowed: boolean;
  onPlanSelect: (plan: ProductPlan) => void;
  className?: string;
}
function PlanCardBase({
  knackPlan,
  originalPlan,
  planFrequency,
  isDowngradeAllowed,
  onPlanSelect,
  className = ''
}: PlanCardBaseProps) {
  const [t] = useTranslation();

  const planTestId = `change-plan-${knackPlan.name.toLowerCase().replace(/\s/g, '-')}`;

  const supportTypeToDisplay = (plan: ProductPlan) => {
    if (plan.id.includes('enterprise')) {
      return 'Premier';
    }
    switch (plan.name) {
      case 'Starter':
        return 'Basic';
      case 'Pro':
        return 'Priority';
      case 'Corporate':
        return 'Premier';
      default:
        return '';
    }
  };

  const cardClassesBase = 'flex flex-col p-6 sm:p-6 h-[400px]';
  const cardClasses = cn(cardClassesBase, className);

  return (
    <Card className={cardClasses} data-testid={planTestId}>
      <h2 className="mb-2 text-xl font-medium text-emphasis">{knackPlan.name}</h2>
      <span className="mb-6">
        {planFrequency === PLAN_FREQUENCY.Monthly ? (
          t('components.billing.plans.price_month', {
            price: knackPlan.price
          })
        ) : (
          <span>
            <span className="mr-1 text-subtle line-through">
              ${knackPlan.price.toLocaleString()}
            </span>
            {t('components.billing.plans.price_month', {
              price: Math.round((knackPlan.price * 10) / 12)
            })}
          </span>
        )}

        {planFrequency === PLAN_FREQUENCY.Yearly && (
          <p>{t('components.billing.plans.billed_yearly')}</p>
        )}
      </span>
      <div className="mb-6 space-y-3">
        <p>
          <Trans
            i18nKey="components.billing.plans.records_amount"
            values={{ records: knackPlan.records.toLocaleString() }}
          >
            <span className="font-semibold">Records</span>
          </Trans>
        </p>
        <Divider />
        <p>
          <Trans
            i18nKey="components.billing.plans.storage_amount"
            values={{ storage: knackPlan.storage }}
          >
            <span className="font-semibold">Storage</span>
          </Trans>
        </p>
        <Divider />
        <p>
          <Trans i18nKey="components.billing.plans.apps_amount" values={{ apps: knackPlan.apps }}>
            <span className="font-semibold">Apps</span>
          </Trans>
        </p>
        <Divider />
        <p>
          <Trans
            i18nKey="components.billing.plans.support"
            values={{ supportType: supportTypeToDisplay(knackPlan) }}
          >
            <span className="font-semibold">Basic</span> support
          </Trans>
        </p>
      </div>

      {originalPlan && (
        <SelectPlanButton
          knackPlan={knackPlan}
          currentPlan={originalPlan}
          onPlanSelect={onPlanSelect}
          isDowngradeAllowed={isDowngradeAllowed}
        />
      )}
    </Card>
  );
}

function PlanCardRecommended({
  knackPlan,
  originalPlan,
  planFrequency,
  isDowngradeAllowed,
  onPlanSelect,
  className = ''
}: PlanCardBaseProps) {
  const [t] = useTranslation();

  return (
    <div>
      <div className="rounded-t-lg bg-pink-600 p-1 text-center font-semibold text-white">
        {t('components.billing.plans.recommended')}
      </div>
      <PlanCardBase
        knackPlan={knackPlan}
        originalPlan={originalPlan}
        planFrequency={planFrequency}
        isDowngradeAllowed={isDowngradeAllowed}
        onPlanSelect={onPlanSelect}
        className={cn('rounded-t-none border border-pink-600 bg-muted', className)}
      />
    </div>
  );
}

export function PlanCard({
  originalPlan,
  knackPlan,
  planFrequency,
  isDowngradeAllowed,
  onPlanSelect,
  className = ''
}: PlanCardBaseProps) {
  const isRecommendedUpgradePlan = originalPlan?.id === 'trial' && knackPlan.id.startsWith('pro');

  return isRecommendedUpgradePlan ? (
    <PlanCardRecommended
      knackPlan={knackPlan}
      originalPlan={originalPlan}
      planFrequency={planFrequency}
      isDowngradeAllowed={isDowngradeAllowed}
      onPlanSelect={onPlanSelect}
      className={className}
    />
  ) : (
    <PlanCardBase
      knackPlan={knackPlan}
      originalPlan={originalPlan}
      planFrequency={planFrequency}
      isDowngradeAllowed={isDowngradeAllowed}
      onPlanSelect={onPlanSelect}
      className={className}
    />
  );
}
